import { useCallback, useEffect, useState } from 'react';
import type { TravelRules } from '../types/travelRules';
import { fetchTravelRules } from '../api/travelRules';

const CACHE_TTL = 5 * 60 * 1000;
let cachedRules: TravelRules | null = null;
let cacheTimestamp = 0;

const isCacheValid = (): boolean => {
  return cachedRules !== null && Date.now() - cacheTimestamp < CACHE_TTL;
};

export const useTravelRules = () => {
  const [travelRules, setTravelRules] = useState<TravelRules | null>(cachedRules);
  const [loading, setLoading] = useState(!isCacheValid());

  const load = useCallback(async () => {
    if (isCacheValid()) {
      setTravelRules(cachedRules);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const rules = await fetchTravelRules();
      cachedRules = rules;
      cacheTimestamp = Date.now();
      setTravelRules(rules);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useTravelRules] Failed to fetch:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(async () => {
    try {
      const rules = await fetchTravelRules();
      cachedRules = rules;
      cacheTimestamp = Date.now();
      setTravelRules(rules);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useTravelRules] Failed to refetch:', e);
    }
  }, []);

  const clearCache = useCallback(() => {
    cachedRules = null;
    cacheTimestamp = 0;
  }, []);

  return { travelRules, loading, refetch, clearCache };
};
